import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, AlertTriangle, CheckCircle, XCircle, Scale } from 'lucide-react';

const TermsOfServicePage = () => {
  const lastUpdated = "February 12, 2026";
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-indigo-600 transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Title Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-100 rounded-full mb-4">
            <FileText className="w-8 h-8 text-indigo-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Terms of Service</h1>
          <p className="text-gray-500">Last updated: {lastUpdated}</p>
        </div>

        {/* Important Notice */}
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-10">
          <div className="flex items-start gap-4">
            <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-lg font-semibold text-amber-900 mb-2">Important Notice</h2>
              <p className="text-amber-800">
                By using Susu Money, you agree to these Terms of Service. Please read them carefully before creating an account or making any transactions. If you do not agree to these terms, do not use our service.
              </p>
            </div>
          </div>
        </div>

        {/* Terms Sections */}
        <div className="space-y-10">
          {/* Section 1 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 bg-indigo-100 text-indigo-700 rounded-full text-sm font-bold">1</span>
              Acceptance of Terms
            </h2>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-600 leading-relaxed">
                These Terms of Service ("Terms") govern your use of the Susu Money application and website (the "Service") operated by Susu Money ("we," "us," or "our"). By accessing or using our Service, you agree to be bound by these Terms.
              </p>
              <p className="text-gray-600 leading-relaxed mt-4">
                We reserve the right to modify these Terms at any time. We will provide notice of significant changes through the app or via email. Your continued use of the Service after changes constitutes acceptance of the modified Terms.
              </p>
            </div>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 bg-indigo-100 text-indigo-700 rounded-full text-sm font-bold">2</span>
              Description of Service
            </h2>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-600 leading-relaxed">
                Susu Money is a digital savings club platform that allows users to:
              </p>
              <ul className="mt-4 space-y-2 text-gray-600">
                <li>• Create and join savings clubs (Susu groups)</li>
                <li>• Make regular contributions to savings pools</li>
                <li>• Earn rewards through our Guardian program</li>
                <li>• Process payments via Stripe and PayPal</li>
                <li>• Track savings progress and transaction history</li>
              </ul>
            </div>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 bg-indigo-100 text-indigo-700 rounded-full text-sm font-bold">3</span>
              Account Registration
            </h2>
            <div className="space-y-4">
              <p className="text-gray-600">To use certain features of the Service, you must create an account. You agree to:</p>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-green-50 border border-green-100 rounded-xl p-4">
                  <h4 className="font-semibold text-green-900 mb-2 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    You Must
                  </h4>
                  <ul className="space-y-1 text-green-800 text-sm">
                    <li>• Provide accurate, current information</li>
                    <li>• Maintain the security of your account</li>
                    <li>• Notify us of unauthorized access</li>
                    <li>• Be at least 13 years old (or have parental consent)</li>
                  </ul>
                </div>
                
                <div className="bg-red-50 border border-red-100 rounded-xl p-4">
                  <h4 className="font-semibold text-red-900 mb-2 flex items-center gap-2">
                    <XCircle className="w-5 h-5" />
                    You Must Not
                  </h4>
                  <ul className="space-y-1 text-red-800 text-sm">
                    <li>• Share your account credentials</li>
                    <li>• Create multiple accounts</li>
                    <li>• Impersonate others</li>
                    <li>• Use false information</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 bg-indigo-100 text-indigo-700 rounded-full text-sm font-bold">4</span>
              Savings Clubs & Contributions
            </h2>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-600 leading-relaxed">
                When participating in savings clubs:
              </p>
              <ul className="mt-4 space-y-2 text-gray-600">
                <li>• <strong>Protection Deposits:</strong> You must maintain a protection deposit equal to one contribution cycle before joining any club.</li>
                <li>• <strong>Contributions:</strong> You are responsible for making timely contributions as agreed upon when joining a club.</li>
                <li>• <strong>Non-Refundable:</strong> Contributions made to savings pools are generally non-refundable except as specified in club rules.</li>
                <li>• <strong>Club Rules:</strong> Each club may have additional rules set by the club creator that you agree to follow.</li>
              </ul>
            </div>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 bg-indigo-100 text-indigo-700 rounded-full text-sm font-bold">5</span>
              Payments & Fees
            </h2>
            <div className="bg-gray-50 rounded-xl p-6">
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Payment Processing</h4>
                  <p className="text-gray-600 text-sm">
                    All payments are processed through Stripe or PayPal. By making a payment, you agree to their respective terms of service. We do not store your credit card or bank account information.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Bank Account Linking via Plaid</h4>
                  <p className="text-gray-600 text-sm">
                    When you connect a bank account, you authorize us and our financial-data partner, <a href="https://plaid.com/legal/" target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline">Plaid Inc.</a>, to access your account balances, identifying details, and transaction history for the purposes of verifying your identity and tracking your Susu contributions. Your banking credentials are entered into Plaid's flow and are never seen or stored by Susu Money. You may disconnect a linked account at any time from Settings → Bank Accounts; doing so revokes Plaid's access on that item and deletes the encrypted access token from our servers.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Membership Fees</h4>
                  <p className="text-gray-600 text-sm">
                    Basic membership: $5/year or $12/3 years. Premium membership: $20/year or $50/3 years. Fees are non-refundable after a 7-day grace period.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Withdrawal Fees</h4>
                  <p className="text-gray-600 text-sm">
                    Withdrawals may be subject to processing fees depending on the withdrawal method chosen.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 bg-indigo-100 text-indigo-700 rounded-full text-sm font-bold">6</span>
              Guardian Rewards Program
            </h2>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-600 leading-relaxed">
                Our Guardian Rewards program offers interest on protection deposits:
              </p>
              <ul className="mt-4 space-y-2 text-gray-600">
                <li>• Bronze Guardian (up to $99): 2% APY</li>
                <li>• Silver Guardian ($100-$499): 3.5% APY</li>
                <li>• Gold Guardian ($500-$1,999): 5% APY</li>
                <li>• Platinum Guardian ($2,000+): 7% APY</li>
              </ul>
              <p className="text-gray-600 leading-relaxed mt-4">
                <strong>Note:</strong> APY rates are subject to change with 30 days notice. Interest is calculated daily and credited monthly. This is not a bank account and funds are not FDIC insured.
              </p>
            </div>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 bg-indigo-100 text-indigo-700 rounded-full text-sm font-bold">7</span>
              Prohibited Activities
            </h2>
            <div className="bg-red-50 border border-red-100 rounded-xl p-6">
              <p className="text-red-800 mb-4">You agree NOT to use the Service to:</p>
              <ul className="space-y-2 text-red-800">
                <li>• Engage in money laundering or fraud</li>
                <li>• Violate any applicable laws or regulations</li>
                <li>• Harass, abuse, or harm other users</li>
                <li>• Attempt to gain unauthorized access to our systems</li>
                <li>• Interfere with the proper functioning of the Service</li>
                <li>• Use automated systems to access the Service</li>
                <li>• Transmit viruses, malware, or malicious code</li>
                <li>• Engage in any activity that could damage our reputation</li>
              </ul>
            </div>
          </section>

          {/* Section 8 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 bg-indigo-100 text-indigo-700 rounded-full text-sm font-bold">8</span>
              Termination
            </h2>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-600 leading-relaxed">
                We may suspend or terminate your account at any time for violations of these Terms or for any other reason at our sole discretion. Upon termination:
              </p>
              <ul className="mt-4 space-y-2 text-gray-600">
                <li>• Your right to use the Service will immediately cease</li>
                <li>• We will process any pending withdrawals within 30 days</li>
                <li>• You remain responsible for any outstanding obligations</li>
                <li>• You may request deletion of your personal data</li>
              </ul>
            </div>
          </section>

          {/* Section 9 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 bg-indigo-100 text-indigo-700 rounded-full text-sm font-bold">9</span>
              Disclaimers & Limitations
            </h2>
            <div className="bg-gray-100 rounded-xl p-6">
              <p className="text-gray-700 text-sm leading-relaxed">
                THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. WE DO NOT GUARANTEE UNINTERRUPTED OR ERROR-FREE SERVICE. TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED.
              </p>
              <p className="text-gray-700 text-sm leading-relaxed mt-4">
                OUR LIABILITY IS LIMITED TO THE AMOUNT YOU HAVE PAID US IN THE PAST 12 MONTHS. WE ARE NOT LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, OR CONSEQUENTIAL DAMAGES.
              </p>
            </div>
          </section>

          {/* Section 10 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 bg-indigo-100 text-indigo-700 rounded-full text-sm font-bold">10</span>
              Dispute Resolution
            </h2>
            <div className="flex items-start gap-4">
              <Scale className="w-8 h-8 text-indigo-600 flex-shrink-0" />
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-600 leading-relaxed">
                  Any disputes arising from these Terms or your use of the Service will be resolved through binding arbitration in accordance with the rules of the American Arbitration Association. You agree to waive any right to a jury trial or class action lawsuit.
                </p>
              </div>
            </div>
          </section>

          {/* Contact Section */}
          <section className="bg-indigo-600 text-white rounded-2xl p-8">
            <h2 className="text-2xl font-bold mb-4">Questions?</h2>
            <p className="text-indigo-100 mb-4">
              If you have any questions about these Terms of Service, please contact us at:
            </p>
            <p className="text-white font-medium">legal@susumoney.com</p>
          </section>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-gray-200 text-center">
          <p className="text-gray-500 text-sm">
            © 2026 Susu Money. All rights reserved.
          </p>
          <div className="flex justify-center gap-6 mt-4">
            <Link to="/privacy" className="text-indigo-600 hover:text-indigo-700 text-sm">Privacy Policy</Link>
            <Link to="/" className="text-indigo-600 hover:text-indigo-700 text-sm">Home</Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TermsOfServicePage;
